/**
 * Service layer — Contacts domain (Round 8)
 *
 * Owns:
 *   Contacts (individuals)  tblEcywoAVDwkOLiK
 *   Organizations           tblsPUYYYOTNkiXUt
 *
 * Both tables currently live in Airtable. When the migration window arrives,
 * only this file changes — all callers remain unaffected.
 *
 * All fields written are stable, non-formula fields only.
 */

import { apiGet, apiGetAll, apiGetOne, apiPost, apiPatch, apiDelete } from './_api.js'

/* ── Dedicated /api/contacts endpoint ────────────────────────────────────
 * Bypass the generic /api/airtable proxy for read operations so we can
 * use server-side search and avoid the extra JSON encode/decode round-trip.
 */
async function contactsGet(params = {}) {
  const token = typeof window.__clerkGetToken === 'function'
    ? await window.__clerkGetToken().catch(() => null)
    : null
  const qs = new URLSearchParams(params).toString()
  const r = await fetch(`/api/contacts${qs ? '?' + qs : ''}`, {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
  })
  if (!r.ok) throw new Error('Contacts request failed: ' + r.status)
  return r.json()
}

/* ── Who added a record (database/368 contact_origins) ─────────────────
 * Airtable credits every Cortex write to one token, so Cortex logs the signed-in person itself.
 * via: 'contacts' | 'lead' | 'lead_conversion' | 'parcel_research'. Best-effort: a failed log
 * never fails the save it describes.
 */
export async function recordContactOrigin(recordKind, airtableId, via, detail) {
  if (!airtableId) return
  try {
    const token = typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null
    await fetch('/api/contacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      body: JSON.stringify({ action: 'origin', recordKind, airtableId, via, detail: detail || null }),
    })
  } catch { /* logging only */ }
}

/** { by, at, via } for a person or organization (following a conversion back to its lead), or null. */
export async function getContactOrigin(id, kind) {
  const r = await contactsGet({ type: 'origin', id, kind: kind === 'org' ? 'org' : 'person' })
  const o = r && r.origin
  return o ? { by: o.added_by_name || 'someone on the team', at: o.created_at, via: o.via } : null
}

/**
 * Browse: returns { contacts, nextOffset }. Pass nextOffset from the
 * previous call to get the next page.
 */
export function browseContacts(offset) {
  return contactsGet(offset ? { type: 'people', offset } : { type: 'people' })
}
export function searchContacts(q) {
  return contactsGet({ type: 'people', q })
}
/**
 * Resolve contact ids → { id: name } map (same source the meeting-minutes PDF uses). Use this to
 * label attendees consistently rather than the team-only {Name (from Team Attendees)} lookup.
 */
export async function getContactNamesByIds(ids) {
  const list = (ids || []).filter(Boolean)
  if (!list.length) return {}
  try {
    const r = await contactsGet({ ids: list.join(',') })
    return (r && r.names) || {}
  } catch { return {} }
}

/** Resolve contact ids → { id: Contact Type } map (e.g. 'Team', 'Former Team', 'Client'). */
export async function getContactTypesByIds(ids) {
  const list = (ids || []).filter(Boolean)
  if (!list.length) return {}
  try {
    const r = await contactsGet({ ids: list.join(',') })
    return (r && r.types) || {}
  } catch { return {} }
}

/** Resolve contact ids → { id: Email } map — used by the Consultant RFP recipient picker. */
export async function getContactEmailsByIds(ids) {
  const list = (ids || []).filter(Boolean)
  if (!list.length) return {}
  try {
    const r = await contactsGet({ ids: list.join(',') })
    return (r && r.emails) || {}
  } catch { return {} }
}
export function browseOrganizations(offset) {
  return contactsGet(offset ? { type: 'orgs', offset } : { type: 'orgs' })
}
export function searchOrganizations(q) {
  return contactsGet({ type: 'orgs', q })
}

/** Resolve organization ids → { id: Organization Name } map (mirrors getContactNamesByIds). */
export async function getOrgNamesByIds(ids) {
  const list = (ids || []).filter(Boolean)
  if (!list.length) return {}
  try {
    const r = await contactsGet({ type: 'orgs', ids: list.join(',') })
    return (r && r.names) || {}
  } catch { return {} }
}

const TBL_CONTACTS = 'tblEcywoAVDwkOLiK'
const TBL_ORGS     = 'tblsPUYYYOTNkiXUt'

/* ── Writable contact fields ──────────────────────────────────────────────
 * Only fields that are safe to write (not formula, rollup, or lookup).
 * Name is the primary field. First Name / Last Name compose it in Airtable.
 */
const CONTACT_WRITE_FIELDS = new Set([
  'First Name', 'Last Name', 'Contact Type', 'Email', 'Phone',
  'Job Title', 'Alder James Role', 'Practice Position',
  'Organizations', 'Organization Contacts',   // the contact↔org link the detail modal edits
  'Slack Member ID', 'Contact Notes', 'Street Address', 'City', 'State', 'Zip',
])

/* Airtable writes return { error } on failure (the proxy passes the body through verbatim). Surface
   it so the modal shows the reason instead of silently closing as if the edit saved. */
function throwOnError(res, what) {
  if (res && res.error) {
    const msg = (res.error && (res.error.message || res.error.type)) || ('Could not save the ' + what)
    throw new Error(msg)
  }
  return res
}

const ORG_WRITE_FIELDS = new Set([
  'Organization Name', 'Type', 'Phone', 'General Email', 'Primary Contact',
  'Website', 'Industry', 'Client Address', 'Client City', 'Client State',
  'Client Zip', 'Notes', 'Client Rating',
])

function pickContact(fields) {
  const out = {}
  Object.keys(fields).forEach(k => { if (CONTACT_WRITE_FIELDS.has(k)) out[k] = fields[k] })
  return out
}

function pickOrg(fields) {
  const out = {}
  Object.keys(fields).forEach(k => { if (ORG_WRITE_FIELDS.has(k)) out[k] = fields[k] })
  return out
}

/** @deprecated — new code uses browseContacts/searchContacts via /api/contacts */
export function getContacts() {
  return browseContacts().then(r => r.contacts || [])
}

/**
 * Internal team contacts (Contact Type = 'Team') for name resolution and pickers.
 * Standalone-safe equivalent of the outline engine's own loadTeamContacts (outline.js) —
 * for surfaces (like the QC tool's standalone /qc route) that don't run inside OutlineView
 * and so have no ctx.teamContacts cache to read.
 */
export async function getTeamContacts() {
  const qs = 'filterByFormula=' + encodeURIComponent('{Contact Type}="Team"') + '&fields%5B%5D=Name&pageSize=100'
  const records = await apiGet(TBL_CONTACTS, qs)
  const list = (records || []).map(r => ({ id: r.id, name: r.fields?.Name || r.id }))
  list.sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
  return list
}

export async function createContact(fields, via = 'contacts') {
  const rec = throwOnError(await apiPost(TBL_CONTACTS, pickContact(fields), { typecast: true }), 'contact')
  if (rec && rec.id) recordContactOrigin('person', rec.id, via)
  return rec
}

export async function updateContact(id, fields) {
  return throwOnError(await apiPatch(TBL_CONTACTS, id, pickContact(fields), { typecast: true }), 'contact')
}

/** Full field set for a single contact (incl. its Organizations link), mirrors getOrganizationDetail. */
export function getContactDetail(id) {
  return apiGetOne(TBL_CONTACTS, id)
}

export async function deleteContact(id) {
  return throwOnError(await apiDelete(TBL_CONTACTS, id), 'contact')
}

/* ── Organizations ────────────────────────────────────────────────────────
 * Sorted by Organization Name asc.
 */

/* Organizations also has ~60 heavy fields (rollups, long Process St URLs).
 * Curate to a small set; the two link fields (Primary Contact, Contacts)
 * are multipleRecordLinks which return rec IDs reliably via the API. */
const ORG_LIST_FIELDS = [
  'Organization Name', 'Type', 'Phone', 'General Email', 'Website', 'Industry',
  'Primary Contact', 'Contacts', 'Client City',
]

/* Primary Contact and Contacts are two independent link fields — Airtable doesn't keep them in
 * sync, and nothing outside this app (the Convert Lead Zap, a direct Airtable edit) guarantees a
 * Primary Contact is ever actually linked into Contacts. Every picker in the app that lists "this
 * org's people" (SendJourneyAccessModal, TeamMemberPickerModal, the Contractor Portal / Consultant
 * RFP recipient pickers, etc.) reads Contacts only — so a Primary Contact who fell out of sync was
 * simply invisible to them, with no way to re-add (2026-08 incident: Kimberly Smith).
 *
 * Two layers: (1) patch the field in memory so every picker is correct on THIS read, with zero
 * wait; (2) fire a real, best-effort write that heals the actual Airtable link — the same
 * `Organization Contacts` write ContactDetailModal's own "add member" action uses — so the org's
 * data is genuinely fixed the first time anyone loads it anywhere in the app, not patched in memory
 * forever. Self-limiting: once healed, `contacts.includes(primary)` is true and this never fires
 * again for that org. Never blocks or fails the read if the write errors.
 */
function withPrimaryContactMember(record) {
  if (!record || !record.fields) return record
  const primary = (record.fields['Primary Contact'] || [])[0]
  if (!primary) return record
  const contacts = record.fields['Contacts'] || []
  if (contacts.includes(primary)) return record
  // Written from the ORGANIZATION side, merged: writing the contact's 'Organization Contacts' to
  // just this org used to drop the contact's other organizations.
  apiPatch(TBL_ORGS, record.id, { 'Contacts': contacts.concat(primary) }).catch(() => {})
  return { ...record, fields: { ...record.fields, Contacts: contacts.concat(primary) } }
}

/**
 * Make these people members of the organization, and the first of them its Primary Contact when
 * it has none (2026-10-27, Matt: Curtis was on the Needles opportunity but not on its client
 * organization, so Client Hub access showed nobody until he was added by hand). Merges, never
 * removes. Returns true when anything changed.
 */
export async function ensureContactsOnOrganization(orgId, contactIds) {
  const want = (contactIds || []).filter(Boolean)
  if (!orgId || !want.length) return false
  const org = await apiGetOne(TBL_ORGS, orgId)
  const f = (org && org.fields) || {}
  const have = f['Contacts'] || []
  const missing = want.filter(id => !have.includes(id))
  const patch = {}
  if (missing.length) patch['Contacts'] = have.concat(missing)
  if (!(f['Primary Contact'] || []).length) patch['Primary Contact'] = [want[0]]
  if (!Object.keys(patch).length) return false
  throwOnError(await apiPatch(TBL_ORGS, orgId, patch), 'organization')
  return true
}

export function getOrganizations() {
  const fqs = ORG_LIST_FIELDS.map(n => 'fields%5B%5D=' + encodeURIComponent(n)).join('&')
  return apiGetAll(
    TBL_ORGS,
    'sort%5B0%5D%5Bfield%5D=Organization+Name&sort%5B0%5D%5Bdirection%5D=asc&' + fqs
  ).then(rows => (rows || []).map(withPrimaryContactMember))
}

/** Organization NAMES whose Type includes `type` (e.g. 'Vendor'), sorted — for spec org-selectors. */
export function getOrganizationNamesByType(type) {
  const want = String(type || '').toLowerCase()
  return getOrganizations().then(rows => (rows || [])
    .filter(r => {
      const t = r.fields && r.fields['Type']
      const arr = Array.isArray(t) ? t : (t ? [t] : [])
      return arr.some(x => String(x).toLowerCase() === want)
    })
    .map(r => (r.fields && r.fields['Organization Name']) || '')
    .filter(Boolean))
}

/** Full field set for a single organization (e.g. Client Rating — a fit-assessment field the
 *  curated list/search endpoints don't return). No fields[] restriction. */
export function getOrganizationDetail(id) {
  return apiGetOne(TBL_ORGS, id).then(withPrimaryContactMember)
}

export async function createOrganization(fields, via = 'contacts') {
  const name = String((fields && fields['Organization Name']) || '').trim()
  if (!name) throw new Error('An organization needs a name.')
  const rec = throwOnError(await apiPost(TBL_ORGS, pickOrg(fields), { typecast: true }), 'organization')
  if (rec && rec.id) recordContactOrigin('org', rec.id, via)
  return rec
}

export async function updateOrganization(id, fields) {
  return throwOnError(await apiPatch(TBL_ORGS, id, pickOrg(fields), { typecast: true }), 'organization')
}

export async function deleteOrganization(id) {
  return throwOnError(await apiDelete(TBL_ORGS, id), 'organization')
}
